import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Spinner,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { Eye, EyeOff } from "lucide-react";
import BreadCrumb from "../../components/Common/BreadCrumb";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import {
  updateTenantSettings,
  resetTenantSettingsStatus,
  resetTenantSettingsError,
  getTenantSettings,
} from "../../store/actions";
import useAuthUser from "../../components/Hooks/useAuthUser";

const TenantSettings = () => {
  const { t } = useTranslation();
  document.title = t("common.pageTitle", {
    page: t("tenantSettings.pageTitle"),
  });

  const dispatch = useDispatch();
  const { userProfile } = useAuthUser();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);

  const { status, error, settings } = useSelector((state) => ({
    status: state.TenantSettings.status,
    error: state.TenantSettings.error,
    settings: state.TenantSettings.settings,
  }));

  // Load tenant settings on component mount
  useEffect(() => {
    dispatch(getTenantSettings());
  }, [dispatch]);

  // Update loading state based on settings
  useEffect(() => {
    if (settings !== null) {
      setLoading(false);
      validation.setValues({
        smsSettingsUID: settings.smsSettingsUID || "",
        smsSettingsPassword: settings.smsSettingsPassword || "",
      });
    }
  }, [settings]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      smsSettingsUID: "",
      smsSettingsPassword: "",
    },
    validationSchema: Yup.object({
      smsSettingsUID: Yup.string().required(t("tenantSettings.uidRequired")),
      smsSettingsPassword: Yup.string().required(
        t("tenantSettings.passwordRequired")
      ),
    }),
    onSubmit: (values) => {
      dispatch(updateTenantSettings(values));
    },
  });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Reset status and errors on component mount
  useEffect(() => {
    dispatch(resetTenantSettingsStatus());
    dispatch(resetTenantSettingsError());
  }, [dispatch]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("tenantSettings.pageTitle")}
          pageTitle={t("tenantSettings.breadcrumbParent")}
        />

        <Row>
          <Col xxl="3"></Col>
          <Col xxl="6">
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">
                  {t("tenantSettings.cardTitle")}
                </h4>
              </CardHeader>
              <CardBody>
                <p className="text-muted mb-4">
                  {t("tenantSettings.description")}
                </p>

                {status === "success" && (
                  <Alert color="success" className="mb-3">
                    {t("tenantSettings.updateSuccess")}
                  </Alert>
                )}

                {error && (
                  <Alert color="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                {loading ? (
                  <div className="text-center py-4">
                    <Spinner color="primary" />
                    <p className="mt-2">
                      {t("tenantSettings.loadingSettings")}
                    </p>
                  </div>
                ) : (
                  <Form onSubmit={validation.handleSubmit}>
                    <Row>
                      <Col md={12}>
                        <FormGroup className="mb-3">
                          <Label
                            htmlFor="smsSettingsUID"
                            className="form-label"
                          >
                            {t("tenantSettings.uidLabel")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            id="smsSettingsUID"
                            name="smsSettingsUID"
                            placeholder={t("tenantSettings.uidPlaceholder")}
                            value={validation.values.smsSettingsUID}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.smsSettingsUID &&
                              validation.errors.smsSettingsUID
                                ? true
                                : false
                            }
                          />
                          {validation.touched.smsSettingsUID &&
                            validation.errors.smsSettingsUID && (
                              <div className="invalid-feedback">
                                {validation.errors.smsSettingsUID}
                              </div>
                            )}
                        </FormGroup>
                      </Col>

                      <Col md={12}>
                        <FormGroup className="mb-3">
                          <Label
                            htmlFor="smsSettingsPassword"
                            className="form-label"
                          >
                            {t("tenantSettings.passwordLabel")}{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <InputGroup>
                            <Input
                              type={showPassword ? "text" : "password"}
                              id="smsSettingsPassword"
                              name="smsSettingsPassword"
                              placeholder={t(
                                "tenantSettings.passwordPlaceholder"
                              )}
                              value={validation.values.smsSettingsPassword}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                validation.touched.smsSettingsPassword &&
                                validation.errors.smsSettingsPassword
                                  ? true
                                  : false
                              }
                            />
                            <InputGroupText
                              onClick={togglePasswordVisibility}
                              style={{ cursor: "pointer" }}
                            >
                              {showPassword ? (
                                <EyeOff size={16} />
                              ) : (
                                <Eye size={16} />
                              )}
                            </InputGroupText>
                          </InputGroup>
                          {validation.touched.smsSettingsPassword &&
                            validation.errors.smsSettingsPassword && (
                              <div className="invalid-feedback d-block">
                                {validation.errors.smsSettingsPassword}
                              </div>
                            )}
                        </FormGroup>
                      </Col>
                    </Row>

                    <div className="mt-4">
                      <Button
                        type="submit"
                        color="primary"
                        disabled={status === "loading"}
                        className="me-2"
                      >
                        {status === "loading" ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            {t("common.saving")}
                          </>
                        ) : (
                          t("tenantSettings.saveSettings")
                        )}
                      </Button>
                      <Button
                        type="button"
                        color="secondary"
                        onClick={() => {
                          validation.resetForm();
                          if (settings) {
                            validation.setValues({
                              smsSettingsUID: settings.smsSettingsUID || "",
                              smsSettingsPassword:
                                settings.smsSettingsPassword || "",
                            });
                          }
                        }}
                      >
                        {t("common.actions.reset")}
                      </Button>
                    </div>
                  </Form>
                )}
              </CardBody>
            </Card>
          </Col>
          <Col xxl="3"></Col>
        </Row>
      </Container>
    </div>
  );
};

export default TenantSettings;
